import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  fetchCategoryStats,
  fetchClusters,
  fetchMeta,
  fetchTechStats,
  fetchTechTrend,
} from "@/api/jobMarket";
import { CategoryChart } from "@/components/jobMarket/CategoryChart";
import { ClusterView } from "@/components/jobMarket/ClusterView";
import { StackedTrendChart } from "@/components/jobMarket/StackedTrendChart";
import { TechBarChart } from "@/components/jobMarket/TechBarChart";
import type {
  CategoryMergeProcessingQueryEngine,
  CategoryStatsResponse,
  CategoryStatItem,
  ClustersResponse,
  Granularity,
  Market,
  MetaResponse,
  TechStatItem,
  TechStatsResponse,
  TechTrendResponse,
} from "@/types/jobMarket";
import type { TelegramUser } from "@/types";

const FIXED_PERIOD = "year" as const;
const TREND_GRANULARITY: Granularity = "quarter";
const TOP_N = 30;

export function JobMarket({ user: _user }: { user: TelegramUser | null }) {
  const [market, setMarket] = useState<Market>("ru");

  const [meta, setMeta] = useState<MetaResponse | null>(null);
  const [techStats, setTechStats] = useState<TechStatsResponse | null>(null);
  const [catStats, setCatStats] = useState<CategoryStatsResponse | null>(null);
  const [techTrend, setTechTrend] = useState<TechTrendResponse | null>(null);
  const [clusters, setClusters] = useState<ClustersResponse | null>(null);
  /** Верхний блок (топ + категории): не ждём кластеры и тренд — они тяжелее на бэкенде */
  const [coreLoading, setCoreLoading] = useState(true);
  const [clustersLoading, setClustersLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMeta().then(setMeta).catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    setCoreLoading(true);
    setClustersLoading(true);
    setTrendLoading(true);
    setTechStats(null);
    setCatStats(null);
    setTechTrend(null);
    setClusters(null);
    setError(null);

    Promise.all([
      fetchTechStats(FIXED_PERIOD, TOP_N, false, market),
      fetchCategoryStats(FIXED_PERIOD, false, market),
    ])
      .then(([t, c]) => {
        setTechStats(t);
        setCatStats(c);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setCoreLoading(false));

    fetchTechTrend(12, TREND_GRANULARITY, market)
      .then(setTechTrend)
      .catch((e: Error) => setError((prev) => prev ?? e.message))
      .finally(() => setTrendLoading(false));

    fetchClusters(market)
      .then(setClusters)
      .catch((e: Error) => setError((prev) => prev ?? e.message))
      .finally(() => setClustersLoading(false));
  }, [market]);

  const availableMarkets = meta?.markets ?? ["ru"];

  return (
    <div className="pt-24 pb-16 px-6 bg-neutral-light min-h-[calc(100vh-var(--header-height))]">
      <div className="max-w-7xl mx-auto w-full">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-heading font-bold text-neutral-text leading-tight">
              Job Market — стек в вакансиях Data-специалиста
            </h1>
            <p className="text-sm text-neutral-text/70 mt-2 max-w-3xl">
              Агрегированные данные по вакансиям помогают оценить, какие технологии и связки стека
              востребованы у работодателей. Страница для понимания рынка современного Data-специалиста:
              от баз данных и оркестрации до облаков и аналитики.
            </p>
          </div>
          <a
            href="https://duckdb.org"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 shrink-0 self-start text-xs text-neutral-text/50 hover:text-primary transition-colors sm:pt-0.5"
          >
            <img src="https://duckdb.org/images/icons/duckdb.svg" alt="" className="h-4 w-4 opacity-90" />
            <span className="font-medium">Powered by DuckDB</span>
          </a>
        </div>

        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neutral-text leading-tight">
              Стек технологий в вакансиях Data Engineer
            </h2>
            <p className="text-neutral-text/50 text-xs mt-0.5">
              СНГ рынок (HH.ru) · топ‑{TOP_N} инструментов за последний год
            </p>
          </div>
          {availableMarkets.length > 1 && (
            <div className="flex rounded-md border border-neutral-border overflow-hidden text-xs shrink-0">
              {(["ru", "global"] as Market[])
                .filter((m) => availableMarkets.includes(m))
                .map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMarket(m)}
                    className={`px-3 py-1.5 font-medium transition-colors ${
                      market === m
                        ? "bg-primary text-white"
                        : "bg-white text-neutral-text/80 hover:bg-neutral-light"
                    }`}
                  >
                    {m === "ru" ? "🇷🇺 RU" : "🌍 Global"}
                  </button>
                ))}
            </div>
          )}
        </div>

        {meta?.has_data === false ? (
          <MetaEmptyState />
        ) : (
          <>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 mb-4 text-sm">
                Ошибка: {error}
              </div>
            )}

            {coreLoading ? (
              <div className="flex items-center justify-center h-64 text-neutral-text/40">
                <div className="text-center">
                  <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <div className="text-xs">Загружаем топ технологий и категории...</div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {techStats?.has_data && (
                  <Row1
                    techData={techStats.data}
                    catData={catStats?.has_data ? catStats.data : []}
                    categoryTotalVacancies={catStats?.current_period?.total_vacancies}
                    categoryMergePe={catStats?.merge_processing_query_engine}
                    topN={TOP_N}
                  />
                )}

                <div className="rounded-xl border border-neutral-border bg-white p-4">
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-sm font-semibold text-neutral-text">Группы по стеку</h3>
                    <span className="text-xs text-neutral-text/50">
                      вакансии с ≥2 технологиями группы · за всё время
                      {!clustersLoading && clusters?.total_vacancies
                        ? ` · всего ${clusters.total_vacancies.toLocaleString()} вак.`
                        : ""}
                    </span>
                  </div>
                  {clustersLoading ? (
                    <SectionSkeleton rows={4} />
                  ) : (
                    <ClusterView clusters={clusters?.clusters ?? []} />
                  )}
                </div>

                <div className="rounded-xl border border-neutral-border bg-white p-4">
                  <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-sm font-semibold text-neutral-text">Динамика за 5 лет</h3>
                    <span className="text-xs text-neutral-text/50">
                      относительная доля в группе · базы данных, оркестрация, вычисления
                    </span>
                  </div>
                  {trendLoading ? (
                    <SectionSkeleton rows={5} tall />
                  ) : (
                    <StackedTrendChart
                      data={techTrend ?? { has_data: false, techs: [], data: [] }}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionSkeleton({ rows, tall }: { rows: number; tall?: boolean }) {
  const h = tall ? "h-40" : "h-16";
  return (
    <div className="space-y-2 animate-pulse" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`rounded-lg bg-neutral-border/50 ${h}`} />
      ))}
    </div>
  );
}

function MetaEmptyState() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
      <h3 className="font-semibold text-amber-800 mb-1">Данные отсутствуют</h3>
      <p className="text-sm text-amber-700 mb-3">
        Файл DuckDB не найден или пуст. Укажите путь к базе в переменной окружения{" "}
        <code className="bg-amber-100 px-1 rounded">DUCKDB_PATH</code> на сервере бэкенда (см.{" "}
        <code className="bg-amber-100 px-1 rounded">backend/.env.example</code>
        ).
      </p>
    </div>
  );
}

function Row1({
  techData,
  catData,
  categoryTotalVacancies,
  categoryMergePe,
  topN,
}: {
  techData: TechStatItem[];
  catData: CategoryStatItem[];
  categoryTotalVacancies?: number;
  categoryMergePe?: CategoryMergeProcessingQueryEngine;
  topN: number;
}) {
  const rightRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<{ getEchartsInstance?: () => { resize: () => void } } | null>(null);
  const rafRef = useRef<number | null>(null);
  const [rightHeight, setRightHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (rightRef.current) setRightHeight(rightRef.current.offsetHeight);
  }, []);

  useEffect(() => {
    const el = rightRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setRightHeight(el.offsetHeight);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      const DURATION = 400;
      const start = performance.now();
      function tick() {
        chartRef.current?.getEchartsInstance?.()?.resize();
        if (performance.now() - start < DURATION) rafRef.current = requestAnimationFrame(tick);
      }
      rafRef.current = requestAnimationFrame(tick);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const OVERHEAD = 66;
  const chartH = rightHeight != null ? Math.max(200, rightHeight - OVERHEAD) : undefined;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
      <div
        className="lg:col-span-3 rounded-xl border border-neutral-border bg-white p-4 flex flex-col"
        style={{ height: rightHeight ?? "auto", transition: "height 0.35s ease", overflow: "visible" }}
      >
        <div className="flex items-baseline justify-between mb-3 flex-shrink-0">
          <h3 className="text-sm font-semibold text-neutral-text">Топ‑{topN} технологий</h3>
          <span className="text-xs text-neutral-text/50">% от числа вакансий в периоде</span>
        </div>
        <div style={{ height: chartH }}>
          <TechBarChart ref={chartRef} data={techData} compare={false} />
        </div>
      </div>

      <div ref={rightRef} className="lg:col-span-2 rounded-xl border border-neutral-border bg-white p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-text">По категориям</h3>
          <span className="text-xs text-neutral-text/50">% вакансий с технологией категории</span>
        </div>
        {catData.length > 0 && (
          <CategoryChart
            data={catData}
            compare={false}
            totalVacancies={categoryTotalVacancies}
            mergeProcessingQueryEngine={categoryMergePe}
          />
        )}
      </div>
    </div>
  );
}
